import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Avatar — initials in a circle, for people lists (Settings → Team).
 *
 * Decorative by default (`aria-hidden`): it always sits next to the person's name, which is
 * what a screen reader should read — announcing "O O, Otto Obst" helps nobody.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  // A phone number or e-mail standing in for a name has no initials worth showing.
  if (!/^\p{L}/u.test(words[0])) return '#';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
}

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  name: string;
  size?: 'sm' | 'md';
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(({ name, size = 'md', className, ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden="true"
    className={cn(
      'tw:inline-flex tw:shrink-0 tw:select-none tw:items-center tw:justify-center tw:rounded-full tw:bg-muted tw:font-sans tw:font-semibold tw:text-foreground',
      size === 'md' ? 'tw:size-9 tw:text-xs' : 'tw:size-7 tw:text-[11px]',
      className
    )}
    {...props}
  >
    {initialsOf(name)}
  </span>
));
Avatar.displayName = 'Avatar';
